import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import useScreen from '@lyearn/core/hooks/useScreen';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { LyLogoWhite } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import { linkStyles } from '@/components/MessageItem/MessageBody/styles';
import { ChatFragment } from '@/features/messages/graphql/fragments/generated/Chat';
import AIResponseFeedback from '@/modules/corporate/modules/channel/views/ChannelDetails/components/MessageListItem/components/AIResponseFeedback';
import { CreatePostMeta, Maybe, SenderTypeEnum } from '@/types/schema';

import { NoteFragment } from '../../graphql/queries/generated/notes';
import usePinnedItems from '../../hooks/usePinnedItems';
import { ChatbotMessageType, ErrorMessageTypeEnum } from '../../types';
import FollowUp from '../FollowUp';
import ScrollToBottomButton from '../ScrollToBottomButton';
import BotMessage from './BotMessage';
import ReasoningMessage from './ReasoningMessage';
import { anchorAuto, anchorNone, scrollContainerHeight } from './styles';
import UserMessage from './UserMessage';

function ErrorMessage({
  textContent,
  id,
  channel,
  allowRegeneration,
  setGenerativeTaskId,
  setMessages,
  setIsWaiting,
  setCurrentNote,
}: ChatbotMessageType & {
  t: TFunction;
  channel?: Maybe<ChatFragment>;
  allowRegeneration: boolean;
  setGenerativeTaskId?: (id: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<ChatbotMessageType[]>>;
  setIsWaiting?: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentNote?: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
}) {
  return (
    <Stack>
      <Stack className="flex-shrink-0 mt-10" justifyContent="start">
        <LyLogoWhite height={30} width={30} />
      </Stack>
      <Stack
        aria-labelledby={id}
        className={cn(
          anchorNone,
          linkStyles,
          'bg-bg-error-light rounded-8 self-start p-12 justify-self-start ml-12 mr-[3.6rem]',
        )}
        direction="col">
        <Typography color="text-text-error-dark" variant="body-long-02">
          {textContent}
        </Typography>
        <AIResponseFeedback
          generationHasFailed
          allowRegeneration={allowRegeneration}
          channel={channel}
          messageId={id}
          setCurrentNote={setCurrentNote}
          setGenerativeTaskId={setGenerativeTaskId}
          setIsWaiting={setIsWaiting}
          setMessages={setMessages}
          textContent={textContent}
        />
      </Stack>
    </Stack>
  );
}

type ChatViewProps = {
  messages: ChatbotMessageType[];
  setMessages: React.Dispatch<React.SetStateAction<ChatbotMessageType[]>>;
  fetchMore: () => void;
  followUpQuestions?: string[];
  onClickFollowUp?: (value: string, meta?: CreatePostMeta) => void;
  isWaiting: boolean;
  channel?: Maybe<ChatFragment>;
  setGenerativeTaskId?: (id: string) => void;
  setIsWaiting?: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentNote?: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  setHideNoteButtons?: React.Dispatch<React.SetStateAction<boolean>>;
  isGeneratingAIResponse?: boolean;
  teachModeEnabled: boolean;
};

function ChatView({
  messages,
  setMessages,
  fetchMore,
  followUpQuestions,
  onClickFollowUp,
  isWaiting,
  channel,
  setGenerativeTaskId,
  setIsWaiting,
  setCurrentNote,
  setHideNoteButtons,
  isGeneratingAIResponse,
  teachModeEnabled,
}: ChatViewProps) {
  const { t } = useTranslation();
  const messageEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(atBottom);
  const { isMobile } = useScreen();

  // Keep the ref in sync with state so consumers reading `atBottomRef.current`
  // (e.g. FollowUp) see the user's real scroll position, not the initial value.
  useEffect(() => {
    atBottomRef.current = atBottom;
  }, [atBottom]);

  const { pinnedItems } = usePinnedItems({ search: '' });

  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const onScroll = debounce((event: Event) => {
      if (!event.target) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = event.target as HTMLElement;
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 20;
      setAtBottom(scrolledToBottom);
    }, 250);

    scrollContainerRef.current.addEventListener('scroll', onScroll);
    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', onScroll);
      }
    };
  }, [fetchMore, scrollContainerRef]);

  const scrollDown = useCallback(() => {
    if (messageEndRef.current) {
      setAtBottom(true);
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [setAtBottom, messageEndRef.current]);

  const scrollDownIfAtBottom = useCallback(() => {
    if (atBottom && messageEndRef.current) {
      setAtBottom(true);
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [atBottom, setAtBottom, messageEndRef.current]);

  useEffect(() => {
    scrollDownIfAtBottom();
  }, [messages]);

  const isLastMessageFromAI = messages[messages.length - 1].senderType === SenderTypeEnum.Ai;

  return (
    <>
      <Stack
        ref={scrollContainerRef}
        className={cn(
          'w-full overflow-y-auto flex-grow',
          scrollContainerHeight,
          isMobile ? 'mt-56' : 'mb-12',
        )}>
        <Stack
          className="w-full max-w-[76.8rem] px-12 pt-20 mx-auto relative"
          direction="col"
          gap={16}>
          {messages.map((message: ChatbotMessageType, index: number) => {
            switch (message.senderType) {
              case SenderTypeEnum.User:
                return <UserMessage key={message.id} t={t} {...message} />;
              case SenderTypeEnum.Reasoning:
                return (
                  <ReasoningMessage
                    key={message.id}
                    t={t}
                    {...message}
                    isLastMessage={index === messages.length - 1}
                    scrollDown={scrollDownIfAtBottom}
                  />
                );
              case SenderTypeEnum.Ai:
                return (
                  <BotMessage
                    key={message.id}
                    allowRegeneration={index === messages.length - 1}
                    channel={channel}
                    pinnedItems={pinnedItems}
                    setCurrentNote={setCurrentNote}
                    setGenerativeTaskId={setGenerativeTaskId}
                    setHideNoteButtons={setHideNoteButtons}
                    setIsWaiting={setIsWaiting}
                    setMessages={setMessages}
                    t={t}
                    teachModeEnabled={teachModeEnabled}
                    onClickFollowUp={onClickFollowUp}
                    {...message}
                  />
                );
              case ErrorMessageTypeEnum.Error:
                return (
                  <ErrorMessage
                    key={message.id}
                    allowRegeneration={index === messages.length - 1}
                    channel={channel}
                    setCurrentNote={setCurrentNote}
                    setGenerativeTaskId={setGenerativeTaskId}
                    setIsWaiting={setIsWaiting}
                    setMessages={setMessages}
                    t={t}
                    {...message}
                  />
                );
              default:
                return null;
            }
          })}
          {!isWaiting && !isGeneratingAIResponse && isLastMessageFromAI ? (
            <FollowUp
              atBottomRef={atBottomRef}
              followUpQuestions={followUpQuestions}
              scrollDown={scrollDown}
              onClickFollowUp={onClickFollowUp}
            />
          ) : null}
          <div ref={messageEndRef} className={cn(anchorAuto, 'mt-12 w-full')} />
        </Stack>
      </Stack>
      <ScrollToBottomButton atBottom={atBottom} onClick={scrollDown} />
    </>
  );
}

export default React.memo(ChatView);
